import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Check, EyeOff, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";

const Stars = ({ value }) => (
  <span className="flex items-center gap-0.5" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i < value ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />
    ))}
  </span>
);

const STATUS_META = {
  pending: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Publié", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  hidden: { label: "Masqué", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
};

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, hidden: 0 });
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    try {
      const [{ data }, s] = await Promise.all([
        api.get("/admin/reviews", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/admin/reviews/stats"),
      ]);
      setItems(data);
      setStats(s.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (id, action) => {
    try {
      await api.post(`/admin/reviews/${id}/moderate`, { action });
      toast.success(action === "approve" ? "Avis publié." : "Avis masqué.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cet avis ?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Avis supprimé.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const FILTERS = [
    ["pending", `En attente (${stats.pending})`],
    ["approved", `Publiés (${stats.approved})`],
    ["hidden", `Masqués (${stats.hidden})`],
    ["all", "Tous"],
  ];

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Avis clients</h1>
        <p className="mt-2 text-neutral-500">
          {"Valide ou masque les avis avant leur publication sur le site."}
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              onClick={() => setFilter(value)}
              data-testid={`filter-reviews-${value}`}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                filter === value ? "border-brand bg-brand text-white" : "border-neutral-300 bg-white hover:border-neutral-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <ul className="mt-5 space-y-3" data-testid="reviews-board">
          {items.length === 0 && (
            <li className="rounded-lg border border-dashed border-neutral-300 px-5 py-8 text-sm text-neutral-500" data-testid="no-reviews">
              {"Aucun avis dans cette catégorie."}
            </li>
          )}
          {items.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            return (
              <li key={r.id} data-testid={`review-item-${r.id}`} className="rounded-xl border border-neutral-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Stars value={r.rating} />
                  <span className="text-xs font-bold text-neutral-500">{r.rating}/5</span>
                  <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${meta.cls}`}>{meta.label}</span>
                  <span className="ml-auto text-xs text-neutral-400">{(r.created_at || "").slice(0, 10)}</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-neutral-800">{"« "}{r.text}{" »"}</p>
                <p className="mt-2 text-xs font-medium text-neutral-500">
                  {r.first_name}{r.city ? ` · ${r.city}` : ""}{r.postal_code ? ` (${r.postal_code})` : ""}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {r.status !== "approved" && (
                    <button onClick={() => moderate(r.id, "approve")} data-testid={`approve-review-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
                      <Check className="h-3.5 w-3.5" /> Publier
                    </button>
                  )}
                  {r.status !== "hidden" && (
                    <button onClick={() => moderate(r.id, "hide")} data-testid={`hide-review-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:border-neutral-500">
                      <EyeOff className="h-3.5 w-3.5" /> Masquer
                    </button>
                  )}
                  <button onClick={() => remove(r.id)} data-testid={`delete-review-${r.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Shell>
  );
}
